import {Modal, Col, Spin, Tag, Divider} from "antd";
import {useFormik} from "formik";
import moment from "moment";
import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import {useStore} from "../../../../hooks/useStore";
import stores from "../../../../store/stores";
import {SelectField} from "../../../_defaultComponents/FilterComponents/FilterComponentStyles";
import {useFetch} from "../../../../hooks/useFetch";
import debounce from "lodash/debounce";

const ShiftCourierUpdateModal = ({shift, visibility, onCancel}) => {
    const {t} = useTranslation(["shiftManagement"]);
    const store = useStore();
    const [searchText, setSearchText] = useState();
    const [confirmLoading, setConfirmLoading] = useState();

    const [loadingCouriers, data, setData] = useFetch({
        fetchFn: stores.courierStore.getCourierList.bind(null, {
            limit: 10,
            skip: 0,
            statuses: [],
            types: [],
            searchText
        }),
        defaultDataValue: {rows: []},
        dependencies: [searchText, visibility],
        condition: !stores.meStore.checkRole("isCompanyUser") && visibility,
        initialCall: visibility && shift.courier?._id
    });

    const formik = useFormik({
        initialValues: {id: shift?._id?.toString(), courierId: shift?.courier?._id.toString()},
        validationSchema: yup.object().shape({
            id: yup.string().required()
        }),
        enableReinitialize: true,
        onSubmit: async values => {
            setConfirmLoading(true);
            try {
                const id = values.id;
                const courierId = values.courierId;
                await store.shiftStore.updateShiftCourier({id, courierId});
                onCancel(true);
            } catch (error) {
                Sentry.captureException(error);
            } finally {
                setConfirmLoading(false);
            }
        }
    });

    useEffect(() => {
        if (shift?.courier?._id.toString()) {
            setData(prev => ({
                ...prev,
                rows: [
                    ...prev.rows,
                    {_id: shift?.courier?._id.toString(), fullname: shift?.courier?.fullname}
                ]
            }));

            setSearchText(shift?.courier?.fullname || "");
        }
    }, [shift?._id?.toString()]);

    const {values, handleSubmit, setFieldValue} = formik;
    const debounceTimeout = 400;
    const onChangeCourier = value => {
        setFieldValue("courierId", value);
    };

    const onSearch = useMemo(
        value => {
            const loadOptions = value => {
                setSearchText(value);
            };
            return debounce(loadOptions, debounceTimeout);
        },
        [debounceTimeout]
    );

    return (
        <Modal
            title={t("actions.updateShiftCourier", {ns: "shiftManagement"})}
            visible={visibility}
            onCancel={() => {
                setSearchText("");
                setData({rows: []});
                onCancel(false);
            }}
            onOk={handleSubmit}
            confirmLoading={confirmLoading}
            okText={t("modal.saveButton", {ns: "common"})}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
        >
            {shift && (
                <>
                    <Col>
                        <p>
                            <b>{t("fields.date", {ns: "common"})}</b> :{" "}
                            {!shift.date ? "-" : moment(shift?.date).format("DD.MM.YYYY")}
                        </p>
                        <p>
                            <b>{t("fields.company", {ns: "common"})}</b> :{" "}
                            {!shift.company ? "-" : shift?.company?.companyName}
                        </p>
                        <p>
                            <b>{t("fields.subcompany", {ns: "common"})}</b> :{" "}
                            {!shift.subcompany ? "-" : shift?.subcompany?.name}
                        </p>
                        <p>
                            <b>{t("fields.courier", {ns: "common"})}</b> :{" "}
                            {!shift.courier || !shift?.courier?.fullname ? (
                                <Tag>Boş Vardiya</Tag>
                            ) : (
                                shift?.courier?.fullname
                            )}
                        </p>
                    </Col>
                    <Divider />
                    <Col>
                        <h4>{t("fields.courier", {ns: "common"})}</h4>
                        <SelectField
                            notFoundContent={loadingCouriers ? <Spin size="small" /> : null}
                            allowClear
                            showSearch
                            placeholder={t("fields.courier", {ns: "common"})}
                            value={values.courierId}
                            loading={loadingCouriers}
                            onSearch={onSearch}
                            onChange={onChangeCourier}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                                option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {data.rows &&
                                data?.rows.map(courier => (
                                    <SelectField.Option
                                        key={courier._id}
                                        value={courier._id}
                                        data-testid="courierSelect_option"
                                    >
                                        {courier?.fullname || ""}
                                    </SelectField.Option>
                                ))}
                        </SelectField>
                    </Col>
                </>
            )}
        </Modal>
    );
};

export default ShiftCourierUpdateModal;
